import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Check, AlertTriangle, Info, X } from 'lucide-react'

// One place for user feedback across the dashboard:
//   const { toast, confirm } = useFeedback()
//   toast.success('Saved')            toast.error(err)
//   if (await confirm({ title, body, confirmLabel, danger, typeToConfirm })) ...
// Replaces the native alert()/confirm() calls that were scattered everywhere.

const FeedbackContext = createContext(null)

export function useFeedback() {
  const ctx = useContext(FeedbackContext)
  if (!ctx) throw new Error('useFeedback must be used inside <FeedbackProvider>')
  return ctx
}

let nextId = 1

export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const [dialog, setDialog] = useState(null)

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), [])
  const push = useCallback((kind, message, opts = {}) => {
    const text = message instanceof Error ? message.message : String(message || '')
    if (!text) return
    const id = nextId++
    setToasts((t) => [...t.slice(-3), { id, kind, text }])
    setTimeout(() => dismiss(id), opts.duration ?? (kind === 'error' ? 6000 : 3200))
  }, [dismiss])

  const toastRef = useRef(null)
  if (!toastRef.current) toastRef.current = {}
  toastRef.current.success = (m, o) => push('success', m, o)
  toastRef.current.error = (m, o) => push('error', m, o)
  toastRef.current.info = (m, o) => push('info', m, o)

  const confirm = useCallback((opts) => new Promise((resolve) => setDialog({ ...opts, resolve })), [])

  return (
    <FeedbackContext.Provider value={{ toast: toastRef.current, confirm }}>
      {children}
      <div className="fixed bottom-5 right-5 z-[200] flex flex-col items-end gap-2 pointer-events-none" aria-live="polite">
        {toasts.map((t) => <Toast key={t.id} toast={t} onClose={() => dismiss(t.id)} />)}
      </div>
      {dialog && <ConfirmDialog {...dialog} onDone={(v) => { dialog.resolve(v); setDialog(null) }} />}
    </FeedbackContext.Provider>
  )
}

function Toast({ toast, onClose }) {
  const Icon = toast.kind === 'success' ? Check : toast.kind === 'error' ? AlertTriangle : Info
  const tone = toast.kind === 'success' ? 'text-[#34d399] bg-[#34d399]/15' : toast.kind === 'error' ? 'text-[#ff6b7d] bg-[#e5233b]/15' : 'text-[#7aa2ff] bg-[#2f6bff]/15'
  return (
    <div className="pointer-events-auto dash-toast flex items-start gap-3 max-w-sm pl-3 pr-2 py-2.5 rounded-2xl">
      <span className={'w-7 h-7 shrink-0 rounded-full flex items-center justify-center ' + tone}><Icon className="w-3.5 h-3.5" /></span>
      <p className="text-[13px] text-white leading-snug pt-1 flex-1">{toast.text}</p>
      <button onClick={onClose} className="w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10" aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button>
    </div>
  )
}

function ConfirmDialog({ title, body, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false, typeToConfirm, onDone }) {
  const [typed, setTyped] = useState('')
  const inputRef = useRef(null)
  const ok = !typeToConfirm || typed.trim().toLowerCase() === String(typeToConfirm).trim().toLowerCase()
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onDone(false) }
    window.addEventListener('keydown', onKey)
    setTimeout(() => inputRef.current?.focus(), 30)
    return () => window.removeEventListener('keydown', onKey)
  }, [onDone])
  return (
    <div className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center sm:p-6" role="alertdialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm dash-fade" onClick={() => onDone(false)} />
      <form
        onSubmit={(e) => { e.preventDefault(); if (ok) onDone(true) }}
        className="relative w-full sm:max-w-md dash-modal rounded-t-[28px] sm:rounded-[28px] p-6 dash-pop"
      >
        <div className={'w-11 h-11 rounded-full flex items-center justify-center mb-4 ' + (danger ? 'bg-[#e5233b]/15 text-[#ff6b7d]' : 'bg-white/10 text-white')}>
          {danger ? <AlertTriangle className="w-5 h-5" /> : <Info className="w-5 h-5" />}
        </div>
        <h2 className="text-[18px] font-semibold text-white tracking-tight">{title}</h2>
        {body && <div className="mt-2 text-[14px] text-[#a6a8ae] leading-relaxed">{body}</div>}
        {typeToConfirm && (
          <label className="block mt-5">
            <span className="text-[12px] text-[#8b8d94]">Type <span className="text-white font-medium">{typeToConfirm}</span> to confirm</span>
            <input ref={inputRef} value={typed} onChange={(e) => setTyped(e.target.value)} className="dash-input mt-2" autoComplete="off" />
          </label>
        )}
        <div className="mt-6 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => onDone(false)} className="dash-btn dash-btn-ghost">{cancelLabel}</button>
          <button type="submit" disabled={!ok} className={'dash-btn ' + (danger ? 'dash-btn-danger' : 'dash-btn-primary')}>{confirmLabel}</button>
        </div>
      </form>
    </div>
  )
}
